import { useMemo } from 'react';

import type { DateRange, DataTransferCostItem } from '@cast/shared-types';
import type {
  ClusterDataTransferSummaryDailyItem,
  WorkloadsCostReportFilters,
} from '@cast/types';
import { sumDataTransferCost, sumDataTransferTraffic } from '@cast/utils';

import { useClusterDataTransferSummaryQuery } from 'hooks/queries/cost';

import { useNetworkCostPrerequisites } from './useNetworkCostPrerequisites';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type NetworkSummaryData = {
  cost: { egress: number; ingress: number; total: number };
  traffic: { egress: number; ingress: number; total: number };
};

type UseNetworkSummaryQueryResult = {
  data: NetworkSummaryData | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isCapabilitiesLoading: boolean;
  hasNetworkAgent: boolean;
  isDataProcessing: boolean;
  error: unknown;
  refetch: () => void;
};

type UseNetworkSummaryQueryArgs = {
  adjustedDateRange: DateRange;
  filter?: WorkloadsCostReportFilters;
};

// ---------------------------------------------------------------------------
// Pure aggregation helpers
// ---------------------------------------------------------------------------

type SumDailyMetricsArgs = {
  metrics?: ClusterDataTransferSummaryDailyItem['egressMetrics'];
};

const sumDailyMetrics = (
  sumDailyMetricsArgs: SumDailyMetricsArgs
): { cost: number; traffic: number } => {
  const { metrics } = sumDailyMetricsArgs;
  const item = (metrics ?? {}) as DataTransferCostItem;
  return {
    cost: sumDataTransferCost(item),
    traffic: sumDataTransferTraffic(item),
  };
};

type AggregateSummaryArgs = {
  dailyItems: ClusterDataTransferSummaryDailyItem[];
};

const aggregateSummary = (
  aggregateSummaryArgs: AggregateSummaryArgs
): NetworkSummaryData => {
  const { dailyItems } = aggregateSummaryArgs;
  const totals = dailyItems.reduce(
    (acc, day) => {
      const egress = sumDailyMetrics({ metrics: day.egressMetrics });
      const ingress = sumDailyMetrics({ metrics: day.ingressMetrics });
      return {
        egressCost: acc.egressCost + egress.cost,
        ingressCost: acc.ingressCost + ingress.cost,
        egressTraffic: acc.egressTraffic + egress.traffic,
        ingressTraffic: acc.ingressTraffic + ingress.traffic,
      };
    },
    { egressCost: 0, ingressCost: 0, egressTraffic: 0, ingressTraffic: 0 }
  );

  return {
    cost: {
      egress: totals.egressCost,
      ingress: totals.ingressCost,
      total: totals.egressCost + totals.ingressCost,
    },
    traffic: {
      egress: totals.egressTraffic,
      ingress: totals.ingressTraffic,
      total: totals.egressTraffic + totals.ingressTraffic,
    },
  };
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useNetworkSummaryQuery = (
  useNetworkSummaryQueryArgs: UseNetworkSummaryQueryArgs
): UseNetworkSummaryQueryResult => {
  const { adjustedDateRange, filter } = useNetworkSummaryQueryArgs;
  const startTime = adjustedDateRange[0].toISOString();
  const endTime = adjustedDateRange[1].toISOString();

  const {
    clusterId,
    hasNetworkAgent,
    isNetworkDataAvailable,
    isCapabilitiesLoading,
    isDataProcessing,
    capabilitiesError,
    refetchCapabilities,
  } = useNetworkCostPrerequisites({ startTime, endTime });

  const { data, isLoading, isFetching, error, refetch } =
    useClusterDataTransferSummaryQuery({
      clusterId,
      startTime,
      endTime,
      filter,
      enabled: !!clusterId && hasNetworkAgent && isNetworkDataAvailable,
    });

  const summary = useMemo(() => {
    const dailyItems = data?.dailyItems;
    if (!dailyItems?.length) return undefined;
    return aggregateSummary({ dailyItems });
  }, [data]);

  return {
    data: summary,
    isLoading: isLoading || isCapabilitiesLoading,
    isFetching,
    isCapabilitiesLoading,
    hasNetworkAgent,
    isDataProcessing,
    error: error || capabilitiesError,
    refetch: () => {
      refetch();
      refetchCapabilities();
    },
  };
};
